import type {
  P8ContinuedSalesPoint,
  P8MonthlyRoasPoint,
} from "../../p8OverviewData";
import type { AdsOrganizeBy, PrototypeVersion } from "../../types";

import type { ChartMonthWindowOptions } from "./chartShared";
import { MonthlyRoasChart } from "./MonthlyRoasChart";
import { NoForecastSalesAndSpendChart } from "./NoForecastSalesAndSpendChart";

const CHART_CELL_CLASS =
  "flex h-[360px] min-h-[360px] w-full min-w-0 shrink-0 flex-col";
// V3 runs the pair 60px shorter. The card's title, legend, and padding are all
// content-sized and the plot is the one `flex-1` child, so the whole reduction
// comes out of the plot rather than out of the spacing around it.
const DENSE_CHART_CELL_CLASS =
  "flex h-[300px] min-h-[300px] w-full min-w-0 shrink-0 flex-col";

type OverviewChartPairProps = {
  version: PrototypeVersion;
  organizeBy: AdsOrganizeBy;
  continuedSales: P8ContinuedSalesPoint[];
  monthlyRoas: P8MonthlyRoasPoint[];
  /** Absent when the cohort page isn't reachable from this view at all. */
  onOpenMonth?: (monthIndex: number) => void;
  /**
   * Passed to both charts, which each hold their own offset within it. The
   * cards page independently, so one card's arrows never move the other.
   */
  monthWindow: ChartMonthWindowOptions;
};

export function OverviewChartPair({
  version,
  organizeBy,
  continuedSales,
  monthlyRoas,
  onOpenMonth,
  monthWindow,
}: OverviewChartPairProps) {
  // Only the sales chart offers Explore on the order-month basis. Its bars are
  // dollars, which the cohort page reports for the same month; the ROAS chart
  // plots a ratio of that month's orders to that month's spend, and nothing on
  // the page it would open states that figure.
  const roasOpenMonth = organizeBy === "order-month" ? undefined : onOpenMonth;
  const cellClass = version === 3 ? DENSE_CHART_CELL_CLASS : CHART_CELL_CLASS;

  return (
    <>
      <div className={cellClass}>
        <NoForecastSalesAndSpendChart
          version={version}
          organizeBy={organizeBy}
          data={continuedSales}
          onOpenMonth={onOpenMonth}
          showContinued
          monthWindow={monthWindow}
        />
      </div>
      <div className={cellClass}>
        <MonthlyRoasChart
          version={version}
          organizeBy={organizeBy}
          data={monthlyRoas}
          onOpenMonth={roasOpenMonth}
          monthWindow={monthWindow}
        />
      </div>
    </>
  );
}
